/* eslint-disable jira/filename-case */
import sum from 'lodash/sum';
import type { EntityLimitType as ProjectEntityLimitType } from '@jira/polaris__domain-project/src/ProjectMetadata.types.tsx';
import type { EntityLimitType as CollectionEntityLimitType } from '@jira/polaris__domain-collection/src/EntityLimitType.types.tsx';
import { VIEW_KIND_SECTION } from '@jira/polaris__domain-view/src/viewKindTimeline.constants.tsx';

import { ENTITY_LIMIT_TYPE as PROJECT_LIMITS } from '@jira/polaris__domain-project/src/entityLimitType.constants.tsx';
import { ENTITY_LIMIT_TYPE as COLLECTION_LIMITS } from '@jira/polaris__domain-collection/src/entityLimitType.constants.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createSelector } from '@atlassian/react-sweet-state';
import type { OutputSelector } from '@atlassian/react-sweet-state/src/utils/create-selector.tsx';
import type { NavigationView, State } from './NavigationView.types.tsx';

export type ContainerProps = {
	containerId: string;
};

export type EntityLimitsHookProp = ContainerProps & {
	entityLimitType: ProjectEntityLimitType | CollectionEntityLimitType | undefined;
};

// Get the limit for a specific entity type
export const getEntityLimits = (state: State, props: EntityLimitsHookProp): number | null => {
	if (!props.entityLimitType) {
		return null;
	}
	return state.entityLimits[props.entityLimitType] ?? null;
};

// Select views for a specific project
const getContainerViews = (state: State, props: ContainerProps) =>
	state.containers[props.containerId].views;

// Count the number of views in a container (project, collection...)
export const getContainerViewsCount: OutputSelector<State, ContainerProps, number> = createSelector(
	getContainerViews,
	(containerViews) => {
		const countViewsRecursive = (view: NavigationView): number => {
			if (view.viewType !== VIEW_KIND_SECTION) {
				return 1;
			}
			return sum(view?.views?.map((v) => countViewsRecursive(v)));
		};

		return sum(containerViews.map((v) => countViewsRecursive(v)));
	},
);

// Count the number of view sets in a container (project, collection...)
export const getContainerViewsetsCount: OutputSelector<State, ContainerProps, number> =
	createSelector(getContainerViews, (containerViews) => {
		const countViewSetsRecursive = (view: NavigationView): number => {
			if (view.viewType === VIEW_KIND_SECTION) {
				return 1 + sum(view?.views?.map((v) => countViewSetsRecursive(v)));
			}

			return 0;
		};

		// 1 for the root view set
		return 1 + sum(containerViews.map((v) => countViewSetsRecursive(v)));
	});

export const getHasReachedLimit: OutputSelector<State, EntityLimitsHookProp, number | null> =
	createSelector(
		(_, props: EntityLimitsHookProp) => props,
		getEntityLimits,
		getContainerViewsCount,
		getContainerViewsetsCount,
		({ entityLimitType }, limit, viewsCount, viewsetsCount) => {
			if (!limit) {
				return null;
			}

			switch (entityLimitType) {
				case PROJECT_LIMITS.VIEWS_PER_PROJECT:
				case COLLECTION_LIMITS.VIEWS_PER_COLLECTION:
					if (limit <= viewsCount) return limit;
					return null;
				case PROJECT_LIMITS.VIEWSETS_PER_PROJECT:
				case COLLECTION_LIMITS.VIEWSETS_PER_COLLECTION:
					if (limit <= viewsetsCount) return limit;
					return null;
				default:
					return null;
			}
		},
	);
